import React, { useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikProps } from 'formik';
import Field from '@cpg-its/ads.form.field';
import Tooltip from '@cpg-its/ads.common.tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Grid, Hidden } from '@material-ui/core';
import Button from '@cpg-its/ads.common.button';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { TcsmStates } from '../../../../root/tcsm-model';
import { AnnualEnrollmentProps, MidYearProps } from '../../models/arc-epi-props';
import { TcsmModal } from '../../../../global/components/tcsm-modal';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { EmploymentState } from '../employment/employment-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { MidYearEnrollment, MidYearEnrollmentFormValue, MidYearPlanType, MidYearQualifyingDateBanner, MidYearQualifyingDateExtenuatingCircumstances } from './mid-year-model';
import { AERelationshipCode, AETierCalculations, Enrollment, ProcessingStatus } from '../../../annual-enrollment/models/annual-enrollment-models';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { tcsmColors } from '../../../../global/constants/theme/theme-constants/colors';
import { getDependentCountByRelationship } from '../../../annual-enrollment/helpers/annual-enrollment-helpers';
import { getAnnualEnrollmentTierCalc } from '../../../../global/ducks/mlps-api';
import { setMYCalcTiers } from './mid-year-slices';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormField } from '../../../../global/components/form-element/form-field';
import { AccountInfoRoutes } from '../../../account-info/constants/routes';
import { ShouldShowAdminCafeteriaAcknowledgement, getPlanFormData, getRemainingCharacterCounter, hasAvailablePlans, planExists } from './mid-year-helpers';
import { MidYearPlanSelection } from './mid-year-plan-selection';
import { BillingAccount } from '../billing-accounts/billing-accounts-models';
import { midYearValidationSchema } from './mid-year-validation-schema';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { EnrollmentErrors, EnrollmentTypes } from '../../../annual-enrollment/components/annual-enrollment-errors';
import { submitMidYearEnrollment } from './mid-year-requests';
import { AnnualEnrollmentRoutes } from '../../../annual-enrollment/constants/annual-enrollment-routes';
import { gridPageStyles } from '../../../../global/constants/theme/styles/layout/grid-page-layout';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { labelSupplementStyles } from '../../../../global/constants/theme/styles/components/label-supplement-styles';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { adjustForLocalOffset, getCurrentTimeNY, getNumberOfDays } from '../../../../global/helpers/date-helpers';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { useMidYearFatalError } from './hooks/useMidYearFatalError';
import { useMidYearLockState } from './hooks/useMidYearState';

interface Props {
	hideRelationshipLink: boolean;
}
export function MidYearForm(props: Props) {
	const dispatch = useDispatch();
	const pageClasses = gridPageStyles();
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const midYearSettings = useSelector<TcsmStates, MidYearProps>(a => a.epiProps?.midYearSettingsState?.epiContent);
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(a => a.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const employmentState = useSelector<TcsmStates, EmploymentState>(a => a.employmentState);
	const billingAccounts = useSelector<TcsmStates, BillingAccount[]>(a => a.billingAccountsState.billingAccounts);
	const groupId = useSelector<TcsmStates, number>(a => a.groupsState?.currentGroup?.group_id);
	const midYearState = useSelector<TcsmStates, MidYearEnrollment>(a => a.midYearEnrollment?.currentMidYearEnrollment);
	const midYearTier = useSelector<TcsmStates, {
		[MidYearPlanType.Medical]: AETierCalculations;
		[MidYearPlanType.Dental]: AETierCalculations;
	}>(a => a.midYearEnrollment?.tierCalculations);
	const participant = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`);
	const relationShipPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${AccountInfoRoutes.RelationshipsSegment}`);
	const groupSearchPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.GroupSearch}`);
	const [open, setOpen] = useState(false);
	const location = useLocation();
	const routeMatch = useRouteMatch();
	const gpsItem = useGpsType(GpsType.MID_YEAR_COVERAGE);
	const history = useHistory();
	const buttonClasses = buttonStyles();
	const fatalError = useMidYearFatalError();
	const midYearLocked = useMidYearLockState();
	const hasPlans = hasAvailablePlans(midYearState);
	const hasUnemployment = !!midYearState?.offerings?.U;
	const [showCafeteria, setShowCafeteria] = useState(false);
	const currentDate = getCurrentTimeNY();
	const preventButtonDisableValidation = !groupId || !hasPlans;
	const [showExtenuatingCircunstances, setShowExtenuatingCircunstances] = useState(MidYearQualifyingDateBanner.NONE);

	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const clearFormHandler = (formProps: FormikProps<MidYearEnrollmentFormValue>) => {
		formProps.resetForm();
		setOpen(false);
		window.scrollTo(0, 0);
		setShowExtenuatingCircunstances(MidYearQualifyingDateBanner.NONE);
		setShowCafeteria(false);
	};

	if (!employmentState?.currentEmployee) {
		return (
			<Redirect to=
				{
					{
						pathname: participant,
						state: {
							actionLink: location.pathname,
						},
					}
				}
			/>);
	}

	if (fatalError) {
		return <WysiwygWithReactLink content={fatalError} />;
	}

	if (midYearState?.form_record?.processing_status === ProcessingStatus.Completed || midYearState?.form_record?.processing_status === ProcessingStatus.InProgress) {
		return <Redirect to={`${routeMatch.path}${AnnualEnrollmentRoutes.Confirmation}`} />;
	}

	const getInitialCoverageValues = (typeKey: MidYearPlanType) => {
		const coverage: string[] = [];

		if (midYearState?.enrollments_base) {
			const eligibleDependents = midYearState?.eligible_dependents;
			for (let i = 0; i < eligibleDependents?.length ?? 0; i += 1) {
				const dependentGuid = eligibleDependents[i]?.person?._guid;
				if (midYearState?.dep_enrollments_base[dependentGuid][typeKey] === YesNoIndicators.Yes) {
					coverage.push(dependentGuid);
				}
			}
		}

		return coverage;
	};

	const getRelationshipLabel = (code: AERelationshipCode) => {
		switch (code) {
			case AERelationshipCode.Child:
				return midYearSettings?.childRelationLabel;
			case AERelationshipCode.Disabled:
				return midYearSettings?.disabledChildRelationLabel;
			case AERelationshipCode.StepChild:
				return midYearSettings?.stepChildRelationLabel;
			case AERelationshipCode.DomesticPartner:
				return midYearSettings?.domesticPartnerRelationLabel;
			case AERelationshipCode.Spouse:
				return midYearSettings?.spouseRelationLabel;
			default:
				return '';
		}
	};

	const medicalPlanExists = planExists(midYearState, MidYearPlanType.Medical);
	const dentalPlanExists = planExists(midYearState, MidYearPlanType.Dental);
	const groupLifePlanExists = planExists(midYearState, MidYearPlanType.Group);
	//const suppLifePlanExists = planExists(midYearState, MidYearPlanType.SuppLife);
	//const suppSpousePlanExists = planExists(midYearState, MidYearPlanType.SuppSpouse);
	const longDisalblePlanExists = planExists(midYearState, MidYearPlanType.LongTermDisable);
	const shortDisablePlanExists = planExists(midYearState, MidYearPlanType.ShortTermDisable);
	const unEmploymentPlanExists = planExists(midYearState, MidYearPlanType.Unemployment);

	const initialValues: MidYearEnrollmentFormValue = {
		date_of_qualifying_event: midYearState?.global_base?.date_of_qualifying_event ? adjustForLocalOffset(new Date(midYearState?.global_base?.date_of_qualifying_event)).toString() : null,

		[MidYearPlanType.Medical]: getInitialCoverageValues(MidYearPlanType.Medical),
		[MidYearPlanType.Dental]: getInitialCoverageValues(MidYearPlanType.Dental),

		...getPlanFormData('medicalPlan', medicalPlanExists, midYearState, MidYearPlanType.Medical),
		...getPlanFormData('dentalPlan', dentalPlanExists, midYearState, MidYearPlanType.Dental),
		...getPlanFormData('groupLifePlan', groupLifePlanExists, midYearState, MidYearPlanType.Group),
		//...getPlanFormData('supplementalLifePlan', suppLifePlanExists, midYearState, MidYearPlanType.SuppLife),
		//...getPlanFormData('supplementalSpousePlan', suppSpousePlanExists, midYearState, MidYearPlanType.SuppSpouse),
		...getPlanFormData('longTermDisabilityPlan', longDisalblePlanExists, midYearState, MidYearPlanType.LongTermDisable),
		...getPlanFormData('shortTermDisabilityPlan', shortDisablePlanExists, midYearState, MidYearPlanType.ShortTermDisable),
		...getPlanFormData('unemploymentInsurancePlan', unEmploymentPlanExists, midYearState, MidYearPlanType.Unemployment),
		extenuatingCircumstances: '',
		cafeteriaAcknowledgement: false,

	} as MidYearEnrollmentFormValue;

	const getSecondaryLabel = (checked: boolean, groupName: MidYearPlanType, value: string) => {
		if (checked && !initialValues[groupName]?.includes(value)) {
			return ['Add', tcsmColors.green];
		}

		if (!checked && initialValues[groupName]?.includes(value)) {
			return ['Remove', tcsmColors.red];
		}

		return null;
	};

	const calculateTiers = (groupName: MidYearPlanType, selectedPeople: string[]) => {

		const tierCount = midYearState.offerings[groupName]?.tier_count;
		const spouseCount = getDependentCountByRelationship(selectedPeople, midYearState.eligible_dependents, [AERelationshipCode.Spouse, AERelationshipCode.DomesticPartner]);
		const childCount = getDependentCountByRelationship(selectedPeople, midYearState.eligible_dependents, [AERelationshipCode.Child, AERelationshipCode.StepChild, AERelationshipCode.Disabled]);

		dispatch(getAnnualEnrollmentTierCalc(groupId, tierCount, spouseCount, childCount, (response) => {
			dispatch(setMYCalcTiers(response, groupName));
		}));
	};

	const onCheckboxChange = (e, groupName: MidYearPlanType, fieldArrayProps: FieldArrayRenderProps, formProps: FormikProps<MidYearEnrollmentFormValue>) => {
		const values = formProps.values[groupName];
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
			calculateTiers(groupName, [...values, value]);

		} else {
			fieldArrayProps.remove(values.indexOf(value));
			calculateTiers(groupName, values.filter(x => x !== value));
		}
	};

	const hasExtenuatingCircunstances = () => {
		return showExtenuatingCircunstances === MidYearQualifyingDateBanner.INFO;
	};

	const handleChangeDisplayCafeteria = (values) => {

		if (values?.date_of_qualifying_event === null || values?.date_of_qualifying_event === undefined) return;

		const numberOfDays = getNumberOfDays(values?.date_of_qualifying_event, currentDate);

		setShowCafeteria(ShouldShowAdminCafeteriaAcknowledgement(initialValues, values, numberOfDays));

		if (numberOfDays < MidYearQualifyingDateExtenuatingCircumstances.MINIMUM) setShowExtenuatingCircunstances(MidYearQualifyingDateBanner.NONE);

		if (numberOfDays > MidYearQualifyingDateExtenuatingCircumstances.MINIMUM && numberOfDays < MidYearQualifyingDateExtenuatingCircumstances.MAXIMUM) setShowExtenuatingCircunstances(MidYearQualifyingDateBanner.INFO);

		if (numberOfDays > MidYearQualifyingDateExtenuatingCircumstances.MAXIMUM) setShowExtenuatingCircunstances(MidYearQualifyingDateBanner.WARNING);

	};

	const onHandleDisableSubmit = (values: MidYearEnrollmentFormValue) => {

		const numberOfDays = getNumberOfDays(values?.date_of_qualifying_event, currentDate);

		if (midYearLocked) return true;

		if (showCafeteria && values?.cafeteriaAcknowledgement !== true) return true;

		if (values?.date_of_qualifying_event !== null && numberOfDays > MidYearQualifyingDateExtenuatingCircumstances.MAXIMUM) return true;

		if (values?.date_of_qualifying_event !== null && (numberOfDays > MidYearQualifyingDateExtenuatingCircumstances.MINIMUM && numberOfDays < MidYearQualifyingDateExtenuatingCircumstances.MAXIMUM) && !values?.extenuatingCircumstances ) return true;

		return false;
	};

	const renderCoverage = (formProps: FormikProps<MidYearEnrollmentFormValue>, groupName: MidYearPlanType, groupLabel: string) => {

		return (
			<>
				<FieldArray
					name={groupName}
					render={(fieldArrayProps) => (
						<>

							<h3>{groupLabel}</h3>


							{midYearState.eligible_dependents?.map(dep => {
								const dependentChecked = formProps?.values[groupName]?.includes(dep?.person?._guid);
								const dependentSecondaryLabelOptions = getSecondaryLabel(dependentChecked, groupName, dep?.person?._guid);

								return (
									<Field
										id={`${groupName}-${dep.person?._guid}`}
										key={`${groupName}-${dep.person?._guid}`}
										name={groupName}
										label={`${dep?.person?.first} ${dep?.person?.middle} ${dep?.person?.last}  ${dep?.person?.suffix}`}
										secondaryLabel={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[0]}
										secondaryLabelColor={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[1]}
										description={getRelationshipLabel(dep?.relationship_code)}
										component='checkBox'										
										value={dep?.person?._guid}
										checked={dependentChecked}
										checkBoxGroup
										onChange={(e) => onCheckboxChange(e, groupName, fieldArrayProps, formProps)}
										disabled={midYearLocked}
									/>
								);
							})}
						</>
					)}
				/>
				<p>{midYearSettings.terminateCoverageHelperText}</p>

			</>
		);
	};

	const renderComments = (formProps: FormikProps<MidYearEnrollmentFormValue>) => {
		const labelClasses = labelSupplementStyles();
		return (
			<DividerSection removeDivider>
				<FormGroup>
					<ContentSpacer>
						<h5>{annualEnrollmentSettings.myNotesLabel}</h5>
						<FormField
							name='memo'
							label=''
							component='input'
							multiline
							rows={5}
							touched={formProps.touched}
							errors={formProps.errors}
							disabled={midYearLocked}

						/>
						<Grid container justifyContent='flex-end'>
							<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.memo?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
						</Grid>
					</ContentSpacer>
					<ContentSpacer>
						<div className={labelClasses.labelLayout}>
							<h5>{annualEnrollmentSettings.specialRequestsLabel}</h5>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={midYearSettings.specialRequestTooltip} />
							</div>
						</div>

						<p className='custom-label'>{annualEnrollmentSettings.specialRequestsDescription}</p>

						<FormField
							name='specialRequests'
							component='input'
							multiline
							rows={5}
							touched={formProps.touched}
							errors={formProps.errors}
							disabled={midYearLocked}

						/>
						<Grid container justifyContent='flex-end'>
							<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.specialRequests?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
						</Grid>
					</ContentSpacer>

					{
						hasExtenuatingCircunstances() && (
							<>
								<ContentSpacer>
									<FormField
										name='extenuatingCircumstances'
										component='input'
										label={midYearSettings.extenuatingCircumstancesLabel}
										multiline
										required={showExtenuatingCircunstances === MidYearQualifyingDateBanner.INFO}
										rows={5}
										touched={formProps.touched}
										errors={formProps.errors}
										disabled={midYearLocked}
										onchange={onHandleDisableSubmit}
										placeholder={midYearSettings.extenuatingCircumstancesPlaceholder}
									/>
									<Grid container justifyContent='flex-end'>
										<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.extenuatingCircumstances?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
									</Grid>
								</ContentSpacer>
							</>
						)
					}

					{
						showCafeteria && (
							<ContentSpacer>
								<FormField
									name='cafeteriaAcknowledgement'
									label={midYearSettings?.midYearCafeteriaAcknowledgement}
									component='checkBox'
									touched={formProps.touched}
									errors={formProps.errors}
									value={formProps?.values?.cafeteriaAcknowledgement ?? false}
									checked={formProps?.values?.cafeteriaAcknowledgement ?? false}
								/>
							</ContentSpacer>
						)
					}

				</FormGroup>
			</DividerSection>
		);
	};

	const renderParticipantsContent = (formProps: FormikProps<MidYearEnrollmentFormValue>) => {
		const hasDependents = midYearState.eligible_dependents && midYearState.eligible_dependents.length > 0;
		const description = hasDependents ? midYearSettings?.participantSelectionDescription : midYearSettings?.noEligibleDependentsDescription;
		const linkText = hasDependents ? midYearSettings.addOrEditRelationshipButtonText : midYearSettings.addDependentsLinkText;
		return (
			<>
				<ContentSpacer>
					<ContentSpacer>
						<WysiwygWithReactLink content={description} />
					</ContentSpacer>
					{
						!props.hideRelationshipLink &&
						<Link className={hasDependents ? '' : buttonClasses.secondaryButton} to={relationShipPath}>{linkText}</Link>
					}
				</ContentSpacer>
				{
					hasDependents &&
					<FormGroup>
						<ContentSpacer>
							{renderCoverage(formProps, MidYearPlanType.Medical, midYearSettings?.medicalCoverageLabel)}
						</ContentSpacer>
						{renderCoverage(formProps, MidYearPlanType.Dental, midYearSettings?.dentalCoverageLabel)}
					</FormGroup>
				}
			</>
		);
	};

	const renderInterruptMessage = () => {
		if (!groupId) {
			return (
				<WysiwygWithReactLink content={midYearSettings?.selectBenefitsGroupMessage} />
			);
		}

		if (!hasPlans) {
			return (
				<WysiwygWithReactLink content={midYearSettings?.noPlansMessage} />
			);
		}

		return null;
	};

	const getSubmitButtonText = () => {
		if (!groupId) return midYearSettings?.selectBenefitsGroupButtonText;

		if (!hasPlans) return midYearSettings?.changeGroupLinkText;

		return midYearSettings?.coverageContinueButtonText;
	};

	const onSubmit = (values: MidYearEnrollmentFormValue) => {
		// redirect to group search if no group selected
		if (!groupId || !hasPlans) {
			history.push({
				pathname: groupSearchPath,
				state: {
					actionLink: location.pathname,
					ignoreEmploymentReset: true,
				},
			});
			return;
		}

		submitMidYearEnrollment(dispatch, values, midYearState, employmentState?.currentEmployee?.guid, groupId);
	};

	const handleRenderQualifyingDateBanner = () => {

		if (showExtenuatingCircunstances === MidYearQualifyingDateBanner.WARNING) {
			return (
				<Banner
					banner={{
						type: 'warning',
						label: <WysiwygWithReactLink content={midYearSettings?.midYearQualifyingEventWarningBannerMessage} />,
					}}
				/>
			);
		}

		return (
			<Banner
				banner={{
					type: 'info',
					label: <WysiwygWithReactLink content={midYearSettings?.midYearQualifyingEventInformationBannerMessage} />,
				}}
			/>
		);

	};

	return (
		<>
			<Formik
				initialValues={initialValues}
				onSubmit={(values: MidYearEnrollmentFormValue) => onSubmit(values)}
				validationSchema={midYearValidationSchema(labelSettings, midYearSettings, !!groupId, hasPlans, hasUnemployment, hasExtenuatingCircunstances())}
				enableReinitialize
			>
				{(formProps: FormikProps<MidYearEnrollmentFormValue>) => {

					const selectedMedTier = (midYearTier && midYearTier[MidYearPlanType.Medical]?.display_tier) || (midYearState?.enrollments_base?.M && midYearState?.enrollments_base?.M[0]?.tier);
					const selectedMedical: Enrollment = {
						account_nbr: formProps?.values?.medicalPlanBillingAccount,
						code: formProps?.values?.medicalPlan,
						rate: midYearState?.offerings?.M?.plans?.filter(a => a.code === formProps?.values?.medicalPlan)[0]?.rates_by_tier[selectedMedTier]?.toFixed(2)?.toString(),
						tier: selectedMedTier,
						effective_date: formProps?.values?.medicalPlanEffectiveDate,
					};

					const selectedDentalTier = (midYearTier && midYearTier[MidYearPlanType.Dental]?.display_tier) || (midYearState?.enrollments_base?.D && midYearState?.enrollments_base?.D[0]?.tier);
					const selectedDental: Enrollment = {
						account_nbr: formProps?.values?.dentalPlanBillingAccount,
						code: formProps?.values?.dentalPlan,
						rate: midYearState?.offerings?.D?.plans?.filter(a => a.code === formProps?.values?.dentalPlan)[0]?.rates_by_tier[selectedDentalTier]?.toFixed(2)?.toString(),
						tier: selectedDentalTier,
						effective_date: formProps?.values?.dentalPlanEffectiveDate,
					};

					const defaultGroup = midYearState?.enrollments_base?.G ? midYearState?.enrollments_base?.G[0] : null;
					const seletedGroup: Enrollment = {
						account_nbr: formProps?.values?.groupLifePlanBillingAccount,
						code: formProps?.values?.groupLifePlan,
						rate: defaultGroup?.rate,
						effective_date: formProps?.values?.groupLifePlanEffectiveDate,
						volume: formProps?.values?.groupLifePlanVolume,
					};

					//const selectedSuppGroup: Enrollment = {
					//    account_nbr: formProps?.values?.supplementalLifePlanBillingAccount,
					//    code: formProps?.values?.supplementalLifePlan,
					//    effective_date: formProps?.values?.supplementalLifePlanEffectiveDate,
					//    volume: formProps?.values?.supplementalLifePlanVolume,
					//};
					//const selectedSuppSpouse: Enrollment = {
					//    account_nbr: formProps?.values?.supplementalSpousePlanBillingAccount,
					//    code: formProps?.values?.supplementalSpousePlan,
					//    effective_date: formProps?.values?.supplementalSpousePlanEffectiveDate,
					//    volume: formProps?.values?.supplementalSpousePlanVolume,
					//};

					const defaultShortTerm = midYearState?.enrollments_base?.W ? midYearState?.enrollments_base?.W[0] : null;
					const selectedShortTerm: Enrollment = {
						account_nbr: formProps?.values?.shortTermDisabilityPlanBillingAccount,
						code: formProps?.values?.shortTermDisabilityPlan,
						rate: defaultShortTerm?.rate,
						effective_date: formProps?.values?.shortTermDisabilityPlanEffectiveDate,
					};

					const defaultLongTerm = midYearState?.enrollments_base?.L ? midYearState?.enrollments_base?.L[0] : null;
					const selectedLongTerm: Enrollment = {
						account_nbr: formProps?.values?.longTermDisabilityPlanBillingAccount,
						code: formProps?.values?.longTermDisabilityPlan,
						rate: defaultLongTerm?.rate,
						effective_date: formProps?.values?.longTermDisabilityPlanEffectiveDate,
					};

					const defaultUnemployment = midYearState?.enrollments_base?.U ? midYearState?.enrollments_base?.U[0] : null;
					const selectedUnemployment: Enrollment = {
						account_nbr: formProps?.values?.unemploymentInsurancePlanBillingAccount,
						code: formProps?.values?.unemploymentInsurancePlan,
						rate: defaultUnemployment?.rate,
						effective_date: formProps?.values?.unemploymentInsurancePlanEffectiveDate,
					};

					return (
						<>
							<div className={pageClasses.gridPageContainer}>
								<Form className='grid-fw-content grid-nested'>

									<FormWrapper
										formikProps={formProps}
										submitText={getSubmitButtonText()}
										cancelText={midYearSettings.clearFormButtonText}
										onCancel={handleOpen}
										preventButtonDisableValidation={preventButtonDisableValidation}
										hideCancelButton={!groupId || !hasPlans}
										submitDisabled={onHandleDisableSubmit(formProps.values)}
									>


										{
											groupId && hasPlans &&
											<>
												<TcsmModal
													open={open}
													handleClose={handleClose}>
													<>
														<div dangerouslySetInnerHTML={{ __html: midYearSettings.clearFormWarningMessage }} />
														<ButtonWrapper flexEnd>
															<Button type='button' buttonStyle='tertiary' text={midYearSettings.cancelModalButtonText} backgroundColor='#808080' onClick={handleClose} />
															<Button type='button' buttonStyle='primary' text={midYearSettings.confirmClearFormButtonText} backgroundColor='#0000FF' onClick={() => { clearFormHandler(formProps); }} />
														</ButtonWrapper>
													</>

												</TcsmModal>
												<div className='grid-main-content'>
													<EnrollmentErrors type={EnrollmentTypes.MIDYEAR} />
													{
														midYearLocked &&
														<ContentSpacer>
															<Banner
																banner={{
																	type: 'warning',
																	label: <WysiwygWithReactLink content={midYearSettings?.midYearFormLockedWarningMessage} />,
																}}
															/>
														</ContentSpacer>
													}
													<PageHeader headerText={midYearSettings.coveragePageTitle} />
													{renderInterruptMessage()}
												</div>
												<Hidden smDown>
													<div className='grid-sidebar'>
														<SideBarContent>
															<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
														</SideBarContent>
													</div>
												</Hidden>
												<div className='grid-main-content'>
													<DividerSection>
														<FormGroup>
															<FormField
																name='date_of_qualifying_event'
																label={midYearSettings.dateOfQualifyingLabel}
																component='datePicker'
																touched={formProps.touched}
																errors={formProps.errors}
																required
																tooltip={{
																	title: midYearSettings.dateOfQualifyingTooltip,
																}}
																autoWidth
																disabled={midYearLocked}
																onChange={handleChangeDisplayCafeteria(formProps.values)}
															/>
														</FormGroup>
														<ContentSpacer>
															{showExtenuatingCircunstances !== MidYearQualifyingDateBanner.NONE ? handleRenderQualifyingDateBanner() : null}
														</ContentSpacer>
													</DividerSection>
													<DividerSection removeDivider sectionText={midYearSettings?.participantSelectionLabel}>
														{renderParticipantsContent(formProps)}
													</DividerSection>
												</div>
												<div className='grid-main-content fw-lg'>
													<DividerSection sectionText={midYearSettings?.planSelectionLabel} topDivider>

														<div dangerouslySetInnerHTML={{ __html: midYearSettings?.planSelectionDescription }} />
														<Grid item xs={12} sm={9} lg={12}>

															<MidYearPlanSelection
																title={midYearSettings?.medicalPlanTitle}
																annualEnrollmentSettings={annualEnrollmentSettings}
																midYearSettings={midYearSettings}
																billingAccounts={billingAccounts}
																offer={midYearState?.offerings?.M}
																selectedEnrollment={selectedMedical}
																defaultEnrollment={midYearState?.enrollments_base?.M ? midYearState?.enrollments_base?.M[0] : null}
																planType={MidYearPlanType.Medical}
																formikProps={formProps}
																propName='medicalPlan'
																midYearLocked={midYearLocked}
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
															/>
														</Grid>
													</DividerSection>
													<DividerSection dashedBorder>
														<Grid item xs={12} sm={9} lg={12}>
															<MidYearPlanSelection
																title={midYearSettings?.dentalPlanTitle}
																annualEnrollmentSettings={annualEnrollmentSettings}
																midYearSettings={midYearSettings}
																billingAccounts={billingAccounts}
																offer={midYearState?.offerings?.D}
																selectedEnrollment={selectedDental}
																defaultEnrollment={midYearState?.enrollments_base?.D ? midYearState?.enrollments_base?.D[0] : null}
																planType={MidYearPlanType.Dental}
																formikProps={formProps}
																propName='dentalPlan'
																midYearLocked={midYearLocked}
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
															/>
														</Grid>
													</DividerSection>

													<DividerSection>
														<Grid item xs={12} sm={9} lg={12}>
															<MidYearPlanSelection
																title={midYearSettings?.groupLifePlanTitle}
																annualEnrollmentSettings={annualEnrollmentSettings}
																midYearSettings={midYearSettings}
																billingAccounts={billingAccounts}
																offer={midYearState?.offerings?.G}
																selectedEnrollment={seletedGroup}
																defaultEnrollment={midYearState?.enrollments_base?.G ? midYearState?.enrollments_base?.G[0] : null}
																planType={MidYearPlanType.Group}
																formikProps={formProps}
																propName='groupLifePlan'
																midYearLocked={midYearLocked}
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
															/>

															{ /*
                                                            <MidYearPlanSelection
                                                                title={midYearSettings?.supplementalLifePlanTitle}
                                                                annualEnrollmentSettings={annualEnrollmentSettings}
                                                                midYearSettings={midYearSettings}
                                                                billingAccounts={billingAccounts}
                                                                offer={midYearState?.offerings?.S}
                                                                selectedEnrollment={selectedSuppGroup}
                                                                defaultEnrollment={midYearState?.enrollments_base?.S ? midYearState?.enrollments_base?.S[0] : null}
                                                                planType={MidYearPlanType.SuppLife}
                                                                formikProps={formProps}
                                                                propName='supplementalLifePlan'
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
                                                            />

                                                            <MidYearPlanSelection
                                                                title={midYearSettings?.supplementalSpouseTitle}
                                                                annualEnrollmentSettings={annualEnrollmentSettings}
                                                                midYearSettings={midYearSettings}
                                                                billingAccounts={billingAccounts}
                                                                offer={midYearState?.offerings?.P}
                                                                selectedEnrollment={selectedSuppSpouse}
                                                                defaultEnrollment={midYearState?.enrollments_base?.P ? midYearState?.enrollments_base?.P[0] : null}
                                                                planType={MidYearPlanType.SuppSpouse}
                                                                formikProps={formProps}
                                                                propName='supplementalSpousePlan'
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
                                                            />

                                                          */  }
														</Grid>
													</DividerSection>
													<DividerSection>
														<Grid item xs={12} sm={9} lg={12}>
															<MidYearPlanSelection
																title={midYearSettings?.longTermTitle}
																annualEnrollmentSettings={annualEnrollmentSettings}
																midYearSettings={midYearSettings}
																billingAccounts={billingAccounts}
																offer={midYearState?.offerings?.L}
																selectedEnrollment={selectedLongTerm}
																defaultEnrollment={midYearState?.enrollments_base?.L ? midYearState?.enrollments_base?.L[0] : null}
																planType={MidYearPlanType.LongTermDisable}
																formikProps={formProps}
																propName='longTermDisabilityPlan'
																disableVolume
																midYearLocked={midYearLocked}
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}

															/>
															<MidYearPlanSelection
																title={midYearSettings?.shortTermTitle}
																annualEnrollmentSettings={annualEnrollmentSettings}
																midYearSettings={midYearSettings}
																billingAccounts={billingAccounts}
																offer={midYearState?.offerings?.W}
																selectedEnrollment={selectedShortTerm}
																defaultEnrollment={midYearState?.enrollments_base?.W ? midYearState?.enrollments_base?.W[0] : null}
																planType={MidYearPlanType.ShortTermDisable}
																formikProps={formProps}
																propName='shortTermDisabilityPlan'
																disableVolume
																midYearLocked={midYearLocked}
																handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
															/>
															{
																midYearState?.offerings?.U &&
																<MidYearPlanSelection
																	title={midYearSettings?.unemploymentInsuranceTitle}
																	annualEnrollmentSettings={annualEnrollmentSettings}
																	midYearSettings={midYearSettings}
																	billingAccounts={billingAccounts}
																	offer={midYearState?.offerings?.U}
																	selectedEnrollment={selectedUnemployment}
																	defaultEnrollment={midYearState?.enrollments_base?.U ? midYearState?.enrollments_base?.U[0] : null}
																	planType={MidYearPlanType.Unemployment}
																	formikProps={formProps}
																	propName='unemploymentInsurancePlan'
																	disableVolume
																	midYearLocked={midYearLocked}
																	handleChangeDisplayCafeteria={handleChangeDisplayCafeteria}
																/>
															}
														</Grid>
													</DividerSection>
												</div>
												<div className='grid-main-content'>
													{renderComments(formProps)}
												</div>
											</>
										}
									</FormWrapper>
								</Form>
							</div>
						</>
					);
				}}
			</Formik>

		</>
	);
}